<template>
  <b-modal
    v-model="showModal"
    title="Klanttoegang beheren"
    size="lg"
    hide-footer
    @hidden="resetModal"
  >
    <b-alert 
      v-if="error" 
      show
      variant="danger" 
      dismissible 
      @dismissed="error = null"
    >
      {{ error }}
    </b-alert>

    <b-alert 
      v-if="successMessage" 
      show
      variant="success" 
      dismissible 
      @dismissed="successMessage = null"
    >
      {{ successMessage }}
    </b-alert>
  
    <div
      v-if="loading"
      class="text-center py-5"
    >
      <b-spinner
        variant="primary"
        style="width: 3rem; height: 3rem;"
      />
      <p class="mt-3 text-muted">
        Gebruikersgegevens worden opgehaald...
      </p>
    </div>
  
    <template v-if="!loading && userData">
      <!-- User Info Display -->
      <b-card
        class="mb-3"
        :bg-variant="dark"
        :border-variant="userData.UID ? 'success bg-dark' : 'warning'"
      >
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h6 class="mb-2">
              <i :class="userData.UID ? 'fa fa-check-circle text-success' : 'fa fa-exclamation-circle text-warning'" />
              Klanttoegang status
            </h6>
            <p class="mb-1">
              <strong>Contactpersoon:</strong> {{ contactpersoon.Naam }}
            </p>
            <p class="mb-1">
              <strong>E-mail:</strong> {{ contactpersoon.Email }}
            </p>
            <p class="mb-0">
              <b-badge :variant="userData.UID ? 'success' : 'secondary'" size="lg">
                {{ userData.UID ? 'ACTIEF' : 'GEEN TOEGANG' }}
              </b-badge>
            </p>
          </div>
          <b-button
            v-if="userData.UID"
            variant="outline-danger"
            size="sm"
            @click="confirmRemoveAccess"
          >
            <i class="fa fa-trash" /> Verwijderen
          </b-button>
        </div>
      </b-card>
  
      <!-- Create Access Section -->
      <b-card
        v-if="!userData.UID"
        class="mb-3"
        border-variant="primary"
      >
        <h5 class="mb-3">
          <i class="fa fa-user-plus" /> Klanttoegang aanmaken
        </h5>
        
        <b-form-group 
          label="Wachtwoord (verplicht)" 
          description="Minimaal 6 karakters"
        >
          <b-input-group>
            <b-form-input 
              v-model="createForm.password" 
              :type="showCreatePassword ? 'text' : 'password'"
              placeholder="Wachtwoord"
              :state="createPasswordState"
            />
            <b-input-group-append>
              <b-button
                variant="outline-secondary"
                @click="showCreatePassword = !showCreatePassword"
              >
                <i :class="showCreatePassword ? 'fa fa-eye-slash' : 'fa fa-eye'" />
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>

        <b-form-group label="Toegangstype">
          <b-form-radio-group
            v-model="createForm.accessType"
            :options="[
              { text: 'Adviesnummer (één relatie)', value: 'adviesnummer' },
              { text: 'Complexnummer (meerdere units)', value: 'complexnummer' }
            ]"
            stacked
          />
        </b-form-group>

        <b-form-group 
          v-if="createForm.accessType === 'adviesnummer'"
          label="Adviesnummer"
          :description="availableAdviesnummers.length > 0 ? 'Beschikbare adviesnummers gevonden' : ''"
        >
          <b-form-input 
            v-model="createForm.adviesnummer" 
            placeholder="Bijv: NOSLI"
            :state="createAdviesnummerState"
            list="adviesnummer-list"
          />
          <datalist id="adviesnummer-list">
            <option v-for="adviesnum in availableAdviesnummers" :key="adviesnum" :value="adviesnum" />
          </datalist>
        </b-form-group>

        <b-form-group 
          v-if="createForm.accessType === 'complexnummer'"
          label="Complexnummer"
          :description="availableComplexnummers.length > 0 ? 'Beschikbare complexnummers gevonden' : ''"
        >
          <b-form-input 
            v-model="createForm.complexnummer" 
            placeholder="Complexnummer"
            :state="createComplexnummerState"
            list="complexnummer-list"
          />
          <datalist id="complexnummer-list">
            <option v-for="complexnum in availableComplexnummers" :key="complexnum" :value="complexnum" />
          </datalist>
        </b-form-group>

        <b-button
          variant="success"
          block
          :disabled="!isCreateFormValid || creating"
          @click="handleCreateAccess"
        >
          <b-spinner v-if="creating" small class="mr-1" />
          <i v-else class="fa fa-plus" />
          {{ creating ? 'Aanmaken...' : 'Klanttoegang aanmaken' }}
        </b-button>
      </b-card>

      <!-- Manage Access Section -->
      <b-card
        v-if="userData.UID"
        class="mb-3"
        border-variant="info"
      >
        <h5 class="mb-3">
          <i class="fa fa-cog" /> Toegangsinstellingen
        </h5>
        
        <b-alert variant="info" show class="mb-3">
          <small><strong>UID:</strong> <code>{{ userData.UID }}</code></small>
        </b-alert>

        <b-form-group label="Toegangstype">
          <b-form-radio-group
            v-model="updateForm.accessType"
            :options="[
              { text: 'Adviesnummer (één relatie)', value: 'adviesnummer' },
              { text: 'Complexnummer (meerdere units)', value: 'complexnummer' }
            ]"
            stacked
          />
        </b-form-group>

        <b-form-group 
          v-if="updateForm.accessType === 'adviesnummer'"
          label="Adviesnummer"
          :description="availableAdviesnummers.length > 0 ? 'Beschikbare adviesnummers gevonden' : ''"
        >
          <b-form-input 
            v-model="updateForm.adviesnummer" 
            placeholder="Bijv: NOSLI"
            :state="updateAdviesnummerState"
            list="adviesnummer-list-update"
          />
          <datalist id="adviesnummer-list-update">
            <option v-for="adviesnum in availableAdviesnummers" :key="adviesnum" :value="adviesnum" />
          </datalist>
        </b-form-group>

        <b-form-group 
          v-if="updateForm.accessType === 'complexnummer'"
          label="Complexnummer"
          :description="availableComplexnummers.length > 0 ? 'Beschikbare complexnummers gevonden' : ''"
        >
          <b-form-input 
            v-model="updateForm.complexnummer" 
            placeholder="Complexnummer"
            :state="updateComplexnummerState"
            list="complexnummer-list-update"
          />
          <datalist id="complexnummer-list-update">
            <option v-for="complexnum in availableComplexnummers" :key="complexnum" :value="complexnum" />
          </datalist>
        </b-form-group>

        <b-button
          variant="primary"
          block
          :disabled="!isUpdateFormValid || updating"
          @click="handleUpdateAccess"
        >
          <b-spinner v-if="updating" small class="mr-1" />
          <i v-else class="fa fa-save" />
          {{ updating ? 'Opslaan...' : 'Instellingen opslaan' }}
        </b-button>
      </b-card>
  
      <!-- Password Reset Section -->
      <b-card
        v-if="userData.UID"
        class="mb-3"
        border-variant="warning"
      >
        <h5 class="mb-3">
          <i class="fa fa-key" /> Wachtwoord wijzigen
        </h5>
        
        <b-form-group 
          label="Nieuw wachtwoord" 
          description="Minimaal 6 karakters"
        >
          <b-input-group>
            <b-form-input 
              v-model="passwordForm.password" 
              :type="showResetPassword ? 'text' : 'password'"
              placeholder="Nieuw wachtwoord"
              :state="resetPasswordState"
            />
            <b-input-group-append>
              <b-button
                variant="outline-secondary"
                @click="showResetPassword = !showResetPassword"
              >
                <i :class="showResetPassword ? 'fa fa-eye-slash' : 'fa fa-eye'" />
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>

        <b-button
          variant="warning"
          block
          :disabled="!resetPasswordState || resettingPassword"
          @click="handleResetPassword"
        >
          <b-spinner v-if="resettingPassword" small class="mr-1" />
          <i v-else class="fa fa-key" />
          {{ resettingPassword ? 'Wijzigen...' : 'Wachtwoord wijzigen' }}
        </b-button>
      </b-card>
  
      <!-- Email-based suggestions -->
      <b-card 
        v-if="emailMatches.length > 0" 
        class="mt-3" 
        border-variant="secondary"
      >
        <h6><i class="fa fa-info-circle" /> Andere contactpersonen met zelfde e-mail</h6>
        <p class="small text-muted mb-2">
          Er zijn {{ emailMatches.length }} andere contactpersoon(en) met dit e-mailadres gevonden.
        </p>
        <b-list-group>
          <b-list-group-item 
            v-for="match in emailMatches" 
            :key="match.id"
            class="d-flex justify-content-between align-items-center"
          >
            <div>
              <strong>{{ match.naam }}</strong><br>
              <small class="text-muted">{{ match.relatieNaam }}</small>
              <br>
              <small v-if="match.adviesnummer">
                <b-badge variant="info">Adviesnr: {{ match.adviesnummer }}</b-badge>
              </small>
              <small v-if="match.complexnummer">
                <b-badge variant="info">Complexnr: {{ match.complexnummer }}</b-badge>
              </small>
            </div>
            <b-button 
              v-if="!userData.UID"
              size="sm" 
              variant="outline-primary"
              @click="applySuggestion(match)"
            >
              <i class="fa fa-copy" /> Kopieer
            </b-button>
          </b-list-group-item>
        </b-list-group>
      </b-card>
    </template>

    <div class="text-center mt-3">
      <b-button variant="secondary" @click="showModal = false">
        <i class="fa fa-times" /> Sluiten
      </b-button>
    </div>

    <!-- Remove Access Confirmation Modal -->
    <b-modal
      v-model="showRemoveConfirm"
      title="Klanttoegang verwijderen?"
      @ok="handleRemoveAccess"
    >
      <p>Weet je zeker dat je de klanttoegang voor <strong>{{ contactpersoon ? contactpersoon.Naam : '' }}</strong> wilt verwijderen?</p>
      <p class="text-danger mb-0">
        <small><i class="fa fa-exclamation-triangle" /> De gebruiker wordt uitgeschakeld en kan niet meer inloggen op de klantsite.</small>
      </p>
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Annuleren
        </b-button>
        <b-button variant="danger" :disabled="removing" @click="ok()">
          <b-spinner v-if="removing" small class="mr-1" />
          {{ removing ? 'Verwijderen...' : 'Verwijderen' }}
        </b-button>
      </template>
    </b-modal>
  </b-modal>
</template>
  
<script>
import { serviceMixin } from "@/mixins/serviceMixin";
  
export default {
  name: "KlanttoegangsModal",
  mixins: [serviceMixin],
  props: {
    contactpersoon: {
      type: Object,
      default: null
    },
    relatieId: {
      type: Number,
      required: true
    },
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showModal: this.value,
      loading: false,
      creating: false,
      updating: false,
      resettingPassword: false,
      removing: false,
      error: null,
      successMessage: null,
      showCreatePassword: false,
      showResetPassword: false,
      showRemoveConfirm: false,
      userData: null,
      emailMatches: [],
      createForm: {
        password: "",
        accessType: "adviesnummer",
        adviesnummer: "",
        complexnummer: ""
      },
      updateForm: {
        accessType: "adviesnummer",
        adviesnummer: "",
        complexnummer: ""
      },
      passwordForm: {
        password: ""
      }
    };
  },
  computed: {
    // Create form validation
    createPasswordState() {
      if (!this.createForm.password) return false;
      return this.createForm.password.length >= 6;
    },
    createAdviesnummerState() {
      if (this.createForm.accessType !== "adviesnummer") return null;
      return this.createForm.adviesnummer.length > 0;
    },
    createComplexnummerState() {
      if (this.createForm.accessType !== "complexnummer") return null;
      return this.createForm.complexnummer.length > 0;
    },
    isCreateFormValid() {
      if (!this.createPasswordState) return false;
      
      if (this.createForm.accessType === "adviesnummer") {
        return this.createAdviesnummerState;
      } else if (this.createForm.accessType === "complexnummer") {
        return this.createComplexnummerState;
      }
      
      return false;
    },

    // Update form validation
    updateAdviesnummerState() {
      if (this.updateForm.accessType !== "adviesnummer") return null;
      return this.updateForm.adviesnummer.length > 0;
    },
    updateComplexnummerState() {
      if (this.updateForm.accessType !== "complexnummer") return null;
      return this.updateForm.complexnummer.length > 0;
    },
    isUpdateFormValid() {
      if (this.updateForm.accessType === "adviesnummer") {
        return this.updateAdviesnummerState;
      } else if (this.updateForm.accessType === "complexnummer") {
        return this.updateComplexnummerState;
      }
      
      return false;
    },

    // Password reset validation
    resetPasswordState() {
      if (!this.passwordForm.password) return null;
      return this.passwordForm.password.length >= 6;
    },

    // Autocomplete options from email matches
    availableAdviesnummers() {
      const adviesNummers = new Set();
      
      // Add from relatie adviesnummers
      this.emailMatches.forEach(match => {
        if (match.adviesnummer && match.adviesnummer !== null) {
          adviesNummers.add(match.adviesnummer);
        }
      });
      
      // Add current Firebase setting if exists
      if (this.userData && this.userData.firestoreData && this.userData.firestoreData.adviesnummer) {
        adviesNummers.add(this.userData.firestoreData.adviesnummer);
      }
      
      return Array.from(adviesNummers).filter(val => val && val.length > 0);
    },

    availableComplexnummers() {
      const complexNummers = new Set();
      
      // Add from relatie complexnummers
      this.emailMatches.forEach(match => {
        if (match.complexnummer && match.complexnummer !== null) {
          complexNummers.add(match.complexnummer);
        }
      });
      
      // Add current Firebase setting if exists
      if (this.userData && this.userData.firestoreData && this.userData.firestoreData.complexnummer) {
        complexNummers.add(this.userData.firestoreData.complexnummer);
      }
      
      return Array.from(complexNummers).filter(val => val && val.length > 0);
    }
  },
  watch: {
    value(newVal) {
      this.showModal = newVal;
      if (newVal && this.contactpersoon) {
        this.fetchUserData();
      }
    },
    showModal(newVal) {
      this.$emit("input", newVal);
    }
  },
  methods: {
    async fetchUserData() {
      this.loading = true;
      this.error = null;

      try {
        const response = await this.get(
          `/relatie/${this.relatieId}/contactpersonen/${this.contactpersoon.Id}/klanttoegang`
        );
        
        this.userData = response.data;
        
        // Pre-fill update form with existing data
        if (this.userData.firestoreData) {
          if (this.userData.firestoreData.adviesnummer) {
            this.updateForm.accessType = "adviesnummer";
            this.updateForm.adviesnummer = this.userData.firestoreData.adviesnummer;
          } else if (this.userData.firestoreData.complexnummer) {
            this.updateForm.accessType = "complexnummer";
            this.updateForm.complexnummer = this.userData.firestoreData.complexnummer;
          }
        }

        // Fetch email matches
        if (response.data.emailMatches) {
          this.emailMatches = response.data.emailMatches;
        }
      } catch (error) {
        this.error = (error.response && error.response.data && error.response.data.error) || "Fout bij ophalen gebruikersgegevens";
      } finally {
        this.loading = false;
      }
    },

    async handleCreateAccess() {
      this.creating = true;
      this.error = null;
      this.successMessage = null;

      try {
        const payload = {
          password: this.createForm.password,
          accessType: this.createForm.accessType,
          adviesnummer: this.createForm.accessType === "adviesnummer" ? this.createForm.adviesnummer : null,
          complexnummer: this.createForm.accessType === "complexnummer" ? this.createForm.complexnummer : null
        };

        await this.post(
          `/relatie/${this.relatieId}/contactpersonen/${this.contactpersoon.Id}/klanttoegang`,
          payload
        );

        this.successMessage = "Klanttoegang succesvol aangemaakt!";
        this.$emit("saved");
        
        // Refresh data
        await this.fetchUserData();
        
        // Reset create form
        this.createForm = {
          password: "",
          accessType: "adviesnummer",
          adviesnummer: "",
          complexnummer: ""
        };
      } catch (error) {
        this.error = (error.response && error.response.data && error.response.data.error) || "Fout bij aanmaken klanttoegang";
      } finally {
        this.creating = false;
      }
    },

    async handleUpdateAccess() {
      this.updating = true;
      this.error = null;
      this.successMessage = null;

      try {
        const payload = {
          password: null, // No password change
          accessType: this.updateForm.accessType,
          adviesnummer: this.updateForm.accessType === "adviesnummer" ? this.updateForm.adviesnummer : null,
          complexnummer: this.updateForm.accessType === "complexnummer" ? this.updateForm.complexnummer : null
        };

        await this.post(
          `/relatie/${this.relatieId}/contactpersonen/${this.contactpersoon.Id}/klanttoegang`,
          payload
        );

        this.successMessage = "Toegangsinstellingen succesvol opgeslagen!";
        this.$emit("saved");
        
        // Refresh data
        await this.fetchUserData();
      } catch (error) {
        this.error = (error.response && error.response.data && error.response.data.error) || "Fout bij opslaan toegangsinstellingen";
      } finally {
        this.updating = false;
      }
    },

    async handleResetPassword() {
      this.resettingPassword = true;
      this.error = null;
      this.successMessage = null;

      try {
        await this.put(
          `/relatie/${this.relatieId}/contactpersonen/${this.contactpersoon.Id}/klanttoegang/password`,
          { password: this.passwordForm.password }
        );

        this.successMessage = "Wachtwoord succesvol gewijzigd!";
        this.passwordForm.password = "";
      } catch (error) {
        this.error = (error.response && error.response.data && error.response.data.error) || "Fout bij wijzigen wachtwoord";
      } finally {
        this.resettingPassword = false;
      }
    },

    confirmRemoveAccess() {
      this.showRemoveConfirm = true;
    },

    async handleRemoveAccess() {
      this.removing = true;
      this.error = null;
      this.successMessage = null;

      try {
        await this.delete(
          `/relatie/${this.relatieId}/contactpersonen/${this.contactpersoon.Id}/klanttoegang`
        );

        this.successMessage = "Klanttoegang succesvol verwijderd!";
        this.$emit("saved");
        
        // Refresh data
        await this.fetchUserData();
        this.showRemoveConfirm = false;
      } catch (error) {
        this.error = (error.response && error.response.data && error.response.data.error) || "Fout bij verwijderen klanttoegang";
        this.showRemoveConfirm = false;
      } finally {
        this.removing = false;
      }
    },

    applySuggestion(match) {
      if (!this.userData.UID) {
        // Apply to create form
        if (match.adviesnummer) {
          this.createForm.accessType = "adviesnummer";
          this.createForm.adviesnummer = match.adviesnummer;
        } else if (match.complexnummer) {
          this.createForm.accessType = "complexnummer";
          this.createForm.complexnummer = match.complexnummer;
        }
      }
    },

    resetModal() {
      this.createForm = {
        password: "",
        accessType: "adviesnummer",
        adviesnummer: "",
        complexnummer: ""
      };
      this.updateForm = {
        accessType: "adviesnummer",
        adviesnummer: "",
        complexnummer: ""
      };
      this.passwordForm = {
        password: ""
      };
      this.error = null;
      this.successMessage = null;
      this.userData = null;
      this.emailMatches = [];
      this.showCreatePassword = false;
      this.showResetPassword = false;
      this.showRemoveConfirm = false;
    }
  }
};
</script>
